/**
 * Module dependencies.
 */

import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover';
import { Editor } from '@tiptap/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * `Props` type.
 */

type Props = {
  children: React.ReactNode;
  editor: Editor;
  onOpenChange: (open: boolean) => void;
  open: boolean;
};

/**
 * Normalize a typed URL (adds `https://` when no scheme was given).
 */

function normalizeHref(value: string): string {
  const href = value.trim();

  if (!href || /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('/') || href.startsWith('#')) {
    return href;
  }

  return `https://${href}`;
}

/**
 * Export `LinkPopover` component — edits the link on the current selection.
 * With no selection and no link under the caret it inserts the URL as a link.
 */

export function LinkPopover({ children, editor, onOpenChange, open }: Props): JSX.Element {
  const [translate] = useTranslation();
  const [url, setUrl] = useState('');
  const isLink = editor.isActive('link');

  useEffect(() => {
    if (open) {
      setUrl((editor.getAttributes('link').href as string | undefined) ?? '');
    }
  }, [editor, open]);

  const apply = useCallback(() => {
    const href = normalizeHref(url);

    if (!href) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else if (editor.state.selection.empty && !editor.isActive('link')) {
      editor
        .chain()
        .focus()
        .insertContent({ marks: [{ attrs: { href }, type: 'link' }], text: href, type: 'text' })
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }

    onOpenChange(false);
  }, [editor, onOpenChange, url]);

  const remove = useCallback(() => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    onOpenChange(false);
  }, [editor, onOpenChange]);

  return (
    <Popover onOpenChange={onOpenChange} open={open}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>

      <PopoverContent align={'start'} className={'w-80 p-2'} onCloseAutoFocus={event => event.preventDefault()}>
        <form
          className={'flex items-center gap-2'}
          onSubmit={event => {
            event.preventDefault();
            apply();
          }}
        >
          <Input
            autoFocus
            className={'h-8 text-sm'}
            onChange={event => setUrl(event.target.value)}
            placeholder={translate('notes.editor.link.placeholder')}
            value={url}
          />

          <Button className={'h-8'} size={'sm'} type={'submit'}>
            {translate('notes.editor.link.apply')}
          </Button>

          {isLink && (
            <Button className={'h-8'} onClick={remove} size={'sm'} type={'button'} variant={'ghost'}>
              {translate('notes.editor.link.remove')}
            </Button>
          )}
        </form>
      </PopoverContent>
    </Popover>
  );
}
